import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import React, { useEffect, useMemo, useRef } from 'react'
import {
  EmptyRemotePlaceholder,
  LayoutStage,
  LocalFloatingTile,
  LocalVideoOffOverlay,
  MainGrid,
  Tile,
  TileLabel,
} from './styles'
import {
  VIDEO_CALL_MEET_TEST_IDS,
  videoCallMeetRemoteLabelTestId,
  videoCallMeetRemoteTileTestId,
  videoCallMeetRemoteVideoOffTestId,
  videoCallMeetRemoteVideoTestId,
} from './testIds'
import { RemoteUserState } from './types'

interface Props {
  localVideoTrack: any
  localAudioMuted: boolean
  localVideoEnabled: boolean
  remoteUsers: RemoteUserState[]
  isFullscreen: boolean
}

export default function VideoCallMeetLayout({
  localVideoTrack,
  localAudioMuted,
  localVideoEnabled,
  remoteUsers,
  isFullscreen,
}: Props) {
  const localRef = useRef<HTMLDivElement | null>(null)
  const remoteRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const sortedRemoteUsers = useMemo(
    () =>
      [...remoteUsers].sort((a, b) => {
        if (a.hasVideo === b.hasVideo) return 0
        return a.hasVideo ? -1 : 1
      }),
    [remoteUsers]
  )

  useEffect(() => {
    if (!localVideoTrack || !localRef.current) return
    localVideoTrack.play(localRef.current, { fit: 'cover' })

    return () => {
      localVideoTrack.stop()
    }
  }, [localVideoTrack])

  useEffect(() => {
    sortedRemoteUsers.forEach((user) => {
      const element = remoteRefs.current[user.uid]
      if (user.videoTrack && element) {
        user.videoTrack.play(element, { fit: 'cover' })
      }
    })

    return () => {
      sortedRemoteUsers.forEach((user) => {
        user.videoTrack?.stop()
      })
    }
  }, [sortedRemoteUsers])

  const fillsMainStage = sortedRemoteUsers.length <= 1

  return (
    <LayoutStage data-testid={VIDEO_CALL_MEET_TEST_IDS.layoutStage}>
      <MainGrid
        data-testid={VIDEO_CALL_MEET_TEST_IDS.mainGrid}
        fillsMainStage={fillsMainStage}
        fullscreenMultiRemote={sortedRemoteUsers.length > 1 && isFullscreen}
      >
        {sortedRemoteUsers.length === 0 && (
          <EmptyRemotePlaceholder
            data-testid={VIDEO_CALL_MEET_TEST_IDS.waitingPlaceholder}
          >
            Aguardando conexão do paciente
          </EmptyRemotePlaceholder>
        )}
        {sortedRemoteUsers.map((user) => (
          <Tile
            key={user.uid}
            data-testid={videoCallMeetRemoteTileTestId(user.uid)}
          >
            <div
              data-testid={videoCallMeetRemoteVideoTestId(user.uid)}
              ref={(node) => {
                remoteRefs.current[user.uid] = node
              }}
              style={{ width: '100%', height: '100%' }}
            />
            {!user.hasVideo && (
              <LocalVideoOffOverlay
                data-testid={videoCallMeetRemoteVideoOffTestId(user.uid)}
              >
                <VideocamOffIcon style={{ fontSize: 54 }} />
              </LocalVideoOffOverlay>
            )}
            <TileLabel data-testid={videoCallMeetRemoteLabelTestId(user.uid)}>
              Paciente
            </TileLabel>
          </Tile>
        ))}
      </MainGrid>

      <LocalFloatingTile data-testid={VIDEO_CALL_MEET_TEST_IDS.localTile}>
        <div
          data-testid={VIDEO_CALL_MEET_TEST_IDS.localVideo}
          ref={localRef}
          style={{ width: '100%', height: '100%' }}
        />
        {!localVideoEnabled && (
          <LocalVideoOffOverlay data-testid={VIDEO_CALL_MEET_TEST_IDS.localVideoOff}>
            <VideocamOffIcon style={{ fontSize: 54 }} />
          </LocalVideoOffOverlay>
        )}
        <TileLabel data-testid={VIDEO_CALL_MEET_TEST_IDS.localLabel}>
          Você {!localVideoEnabled ? '(camera desligada)' : ''}{' '}
          {localAudioMuted ? '(microfone mutado)' : ''}
        </TileLabel>
      </LocalFloatingTile>
    </LayoutStage>
  )
}
